/**
 * RBAC Context Provider
 * 
 * Provides role-based access control throughout the application
 * Based on user profile data from the profile API
 */

// cspell:ignore IRBAC
import React, { useState, useCallback, useMemo, useEffect } from 'react';
import type { 
  RBACContext as IRBACContext, 
  UserProfile, 
  UserAccessPermissions,
  UserRole,
  AppModule,
  AppraisalCycle,
  DeadlineType
} from '../types/rbac';
import { RBACContext } from './rbacContextInstance';

/**
 * Determine user role based on their access permissions
 */
const determineUserRole = (permissions: UserAccessPermissions): UserRole => {
  // HR Admin: Only has admin access
  if (permissions.admin && !permissions.self_appraisal && !permissions.team_review) {
    return 'hr_admin';
  }

  // HOD: Has approvals and team review, but not self appraisal
  if (!permissions.self_appraisal && permissions.nomination_approval && permissions.team_review) {
    return 'hod';
  }

  // Manager: Has self appraisal, nomination approval, and team review
  if (permissions.self_appraisal && permissions.nomination_approval && permissions.team_review) {
    return 'manager';
  }

  // Self: Has self appraisal and nomination review only
  if (permissions.self_appraisal && permissions.nomination_review && !permissions.nomination_approval && !permissions.team_review) {
    return 'self';
  }

  // If permissions don't match any known pattern
  return 'unknown';
};

interface RBACProviderProps {
  children: React.ReactNode;
}

/**
 * RBAC Provider Component
 */
export const RBACProvider: React.FC<RBACProviderProps> = ({ children }) => {
  const [userProfile, setUserProfileState] = useState<UserProfile | null>(null);
  const [appraisalCycle, setAppraisalCycleState] = useState<AppraisalCycle | null>(null);
  const [isLoading, setIsLoading] = useState(true); // Start with true, will be set to false after profile loads
  const [error, setError] = useState<string | null>(null);

  // Derived state
  // Support both 'access' (correct) and 'acesss' (legacy typo) field names
  const permissions = useMemo(() => 
    userProfile?.access || userProfile?.acesss || null, 
    [userProfile]
  );
  const userRole = useMemo(() => 
    permissions ? determineUserRole(permissions) : 'unknown',
    [permissions]
  );

  /**
   * Set user profile from API response
   */
  const setUserProfile = useCallback((profile: UserProfile) => {
    setUserProfileState(profile);
    setIsLoading(false);
    setError(null);
  }, []);

  /**
   * Set appraisal cycle from API response
   */
  const setAppraisalCycle = useCallback((cycle: AppraisalCycle | null) => {
    setAppraisalCycleState(cycle);
    setError(null);
  }, []);

  /**
   * Clear user profile (on logout)
   */
  const clearUserProfile = useCallback(() => {
    setUserProfileState(null);
    setAppraisalCycleState(null);
    setIsLoading(false);
    setError(null);
  }, []);

  /**
   * Check if user has access to a specific module
   */
  const hasAccess = useCallback((module: AppModule | keyof UserAccessPermissions): boolean => {
    if (!permissions) return false;
    
    // Direct permission check
    if (module in permissions) {
      return permissions[module as keyof UserAccessPermissions] === true;
    }
    
    return false;
  }, [permissions]);

  /**
   * Check if user has access to ANY of the specified modules
   */
  const hasAnyAccess = useCallback((modules: (AppModule | keyof UserAccessPermissions)[]): boolean => {
    return modules.some(module => hasAccess(module));
  }, [hasAccess]);

  /**
   * Check if user has access to ALL of the specified modules
   */
  const hasAllAccess = useCallback((modules: (AppModule | keyof UserAccessPermissions)[]): boolean => {
    return modules.every(module => hasAccess(module));
  }, [hasAccess]);

  /**
   * Convenience methods for common access checks
   */
  const canViewSelfAppraisal = useCallback(() => 
    hasAccess('self_appraisal'),
    [hasAccess]
  );

  const canEditOKRs = useCallback(() => 
    hasAccess('self_appraisal_okr'),
    [hasAccess]
  );

  const canEditNominations = useCallback(() => 
    hasAccess('self_appraisal_nomination'),
    [hasAccess]
  );

  const canReviewNominations = useCallback(() => 
    hasAccess('nomination_review'),
    [hasAccess]
  );

  const canApproveNominations = useCallback(() => 
    hasAccess('nomination_approval'),
    [hasAccess]
  );

  const canApproveOKRs = useCallback(() => 
    hasAccess('okr_approval'),
    [hasAccess]
  );

  const canReviewTeam = useCallback(() => 
    hasAccess('team_review'),
    [hasAccess]
  );

  const canAccessAdmin = useCallback(() => 
    hasAccess('admin'),
    [hasAccess]
  );

  const canViewOKR = useCallback(() => 
    hasAccess('view_okr'),
    [hasAccess]
  );

  const getUserRole = useCallback(() => userRole, [userRole]);

  /**
   * Calculate days left from a deadline date
   */
  const calculateDaysLeft = useCallback((deadlineDate: string): number | null => {
    if (!deadlineDate) return null;
    
    const deadline = new Date(deadlineDate);
    if (Number.isNaN(deadline.getTime())) return null;
    
    const today = new Date();
    const deadlineLocal = new Date(deadline.getFullYear(), deadline.getMonth(), deadline.getDate());
    const todayLocal = new Date(today.getFullYear(), today.getMonth(), today.getDate());
    const msPerDay = 24 * 60 * 60 * 1000;
    
    return Math.floor((deadlineLocal.getTime() - todayLocal.getTime()) / msPerDay);
  }, []);

  /**
   * Check if a deadline has passed
   */
  const isDeadlinePassed = useCallback((deadlineType: DeadlineType): boolean => {
    if (!appraisalCycle) return false;
    
    const deadlineMap: Record<DeadlineType, string> = {
      'self_appraisal': appraisalCycle.self_appraisal_deadline,
      'okr_approval': appraisalCycle.okr_approval_deadline,
      'nomination': appraisalCycle.nomination_deadline,
      'nomination_approval': appraisalCycle.nomination_approval_deadline,
      'review': appraisalCycle.review_deadline,
      'hod_review': appraisalCycle.hod_review_deadline,
    };
    
    const deadline = deadlineMap[deadlineType];
    if (!deadline) return false;
    
    const daysLeft = calculateDaysLeft(deadline);
    return daysLeft !== null && daysLeft < 0;
  }, [appraisalCycle, calculateDaysLeft]);

  /**
   * Get days left for a specific deadline
   */
  const getDaysLeft = useCallback((deadlineType: DeadlineType): number | null => {
    if (!appraisalCycle) return null;
    
    const deadlineMap: Record<DeadlineType, string> = {
      'self_appraisal': appraisalCycle.self_appraisal_deadline,
      'okr_approval': appraisalCycle.okr_approval_deadline,
      'nomination': appraisalCycle.nomination_deadline,
      'nomination_approval': appraisalCycle.nomination_approval_deadline,
      'review': appraisalCycle.review_deadline,
      'hod_review': appraisalCycle.hod_review_deadline,
    };
    
    const deadline = deadlineMap[deadlineType];
    return deadline ? calculateDaysLeft(deadline) : null;
  }, [appraisalCycle, calculateDaysLeft]);

  /**
   * Get deadline date for a specific type
   */
  const getDeadlineDate = useCallback((deadlineType: DeadlineType): string | null => {
    if (!appraisalCycle) return null;
    
    const deadlineMap: Record<DeadlineType, string> = {
      'self_appraisal': appraisalCycle.self_appraisal_deadline,
      'okr_approval': appraisalCycle.okr_approval_deadline,
      'nomination': appraisalCycle.nomination_deadline,
      'nomination_approval': appraisalCycle.nomination_approval_deadline,
      'review': appraisalCycle.review_deadline,
      'hod_review': appraisalCycle.hod_review_deadline,
    };
    
    return deadlineMap[deadlineType] || null;
  }, [appraisalCycle]);

  // Log role changes in development
  useEffect(() => {
    if (process.env.NODE_ENV === 'development' && userProfile) {
      console.group('🔐 RBAC User Role Detected');
      console.log('User:', userProfile.name_of_employee);
      console.log('Email:', userProfile.email);
      console.log('Role:', userRole);
      console.log('Permissions:', permissions);
      console.groupEnd();
    }
  }, [userProfile, userRole, permissions]);

  const value: IRBACContext = useMemo(() => ({
    // State
    userProfile,
    permissions,
    userRole,
    appraisalCycle,
    isLoading,
    error,
    // Actions
    setUserProfile,
    setAppraisalCycle,
    clearUserProfile,
    hasAccess,
    hasAnyAccess,
    hasAllAccess,
    canViewSelfAppraisal,
    canEditOKRs,
    canEditNominations,
    canReviewNominations,
    canApproveNominations,
    canApproveOKRs,
    canReviewTeam,
    canAccessAdmin,
    canViewOKR,
    getUserRole,
    isDeadlinePassed,
    getDaysLeft,
    getDeadlineDate,
  }), [
    userProfile,
    permissions,
    userRole,
    appraisalCycle,
    isLoading,
    error,
    setUserProfile,
    setAppraisalCycle,
    clearUserProfile,
    hasAccess,
    hasAnyAccess,
    hasAllAccess,
    canViewSelfAppraisal,
    canEditOKRs,
    canEditNominations,
    canReviewNominations,
    canApproveNominations,
    canApproveOKRs,
    canReviewTeam,
    canAccessAdmin,
    canViewOKR,
    getUserRole,
    isDeadlinePassed,
    getDaysLeft,
    getDeadlineDate,
  ]);

  return (
    <RBACContext.Provider value={value}>
      {children}
    </RBACContext.Provider>
  );
};

export default RBACProvider;
